import React, { Suspense, lazy } from 'react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from './lib/api.js';
import { initBookingAttribution } from './lib/attribution.js';
import { setAnalyticsMeasurementId } from './lib/tracker.js';
import { initClarity, setClarityProjectId } from './lib/clarity.js';
import { setTenantFormattingDefaults } from './lib/formatting.js';
import { setTenantTimePreferences } from './lib/time.js';
import Skeleton, { ProgressSkeletonItems } from './components/Skeleton.jsx';
import Icon from './components/Icon.jsx';
import {
  OffsiteBookingPreviewLoading,
  OffsiteDiscoveryLoading,
} from './components/OffsiteSkeletons.jsx';
import MaintenanceBanner from './components/MaintenanceBanner.jsx';
import UpdateBanner from './components/UpdateBanner.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';

const loadBookingPage = () => import('./pages/BookingPage.jsx');
const loadOffsitePage = () => import('./pages/OffsitePage.jsx');
const BookingPage = lazy(loadBookingPage);
const HomePage = lazy(() => import('./pages/HomePage.jsx'));
const EventsPage = lazy(() => import('./pages/EventsPage.jsx'));
const OffsitePage = lazy(loadOffsitePage);
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage.jsx'));
const CancelledPage = lazy(() => import('./pages/CancelledPage.jsx'));
const PortalPage = lazy(() => import('./pages/PortalPage.jsx'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage.jsx'));

const RouteFallback = () => (
  <main className="page">
    <section className="card card--soft stack" aria-busy="true">
      <Skeleton width={220} height={30} />
      <Skeleton width="68%" height={14} />
      <Skeleton width="52%" height={14} />
    </section>
  </main>
);

const BookingRouteFallback = () => (
  <main className="page page--booking" aria-busy="true" aria-label="Loading booking page">
    <header className="hero hero--fun hero--booking booking-route-fallback__hero">
      <div className="hero-brand">
        <Skeleton width={44} height={44} />
        <Skeleton width={260} height={14} />
      </div>
      <div className="hero-title"><Skeleton width="30%" height={34} /></div>
      <div className="booking-route-fallback__location"><Skeleton width="76%" height="100%" /></div>
      <div className="booking-route-fallback__subtitle"><Skeleton width="100%" height="100%" /></div>
    </header>
    <div className="layout">
      <section className="panel card card--soft stack stack--lg">
        <div className="booking-progress">
          <ProgressSkeletonItems labels={['Package', 'Time & place', 'Details', 'Payment']} />
        </div>
        <section className="step-card">
          <Skeleton width="62%" height={26} />
          <Skeleton width="48%" height={16} />
          <Skeleton width={150} height={16} />
          <div className="packages-grid">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="package-option skeleton-package">
                <div className="package-image">
                  <Skeleton width="100%" height="100%" />
                </div>
                <div className="package-title">
                  <Skeleton width="60%" height={18} />
                  <div className="package-price">
                    <Skeleton width={76} height={22} />
                    <Skeleton width={48} height={12} />
                  </div>
                </div>
                <div className="package-description">
                  <Skeleton className="skeleton-line" width="100%" height={13} />
                  <Skeleton className="skeleton-line" width="92%" height={13} />
                  <Skeleton className="skeleton-line" width="74%" height={13} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </section>
      <aside className="summary">
        <section className="card summary-card stack">
          <Skeleton width={160} height={22} />
          {Array.from({ length: 4 }).map((_, index) => (
            <div className="summary-row" key={`booking-summary-fallback-${index}`}>
              <Skeleton width="34%" height={12} />
              <Skeleton width="50%" height={14} />
            </div>
          ))}
          <div className="summary-divider" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div className="summary-row" key={`booking-price-fallback-${index}`}>
              <Skeleton width="48%" height={12} />
              <Skeleton width={70} height={14} />
            </div>
          ))}
        </section>
      </aside>
    </div>
  </main>
);

const OffsiteRouteFallback = () => (
  <main className="page page--offsite-booking" aria-busy="true" aria-label="Loading event">
    <header className="hero hero--fun offsite-booking__intro">
      <div className="hero-brand">
        <Skeleton width={44} height={44} />
        <Skeleton width={260} height={14} />
      </div>
      <a className="offsite-back-link" href="/events">
        <Icon name="arrowLeft" /> All events
      </a>
      <h1><Skeleton width={260} height={38} /></h1>
      <p><Skeleton width="70%" height={14} /></p>
    </header>
    <div className="offsite-layout">
      <section className="offsite-discovery" aria-label="Loading event sessions">
        <OffsiteDiscoveryLoading calendarCellCount={42} />
      </section>
      <section className="offsite-booking-panel offsite-booking-panel--preview card card--soft">
        <div className="offsite-booking-panel__heading">
          <h2>Reserve your place</h2>
          <p>Review your selected session, then continue to booking.</p>
        </div>
        <OffsiteBookingPreviewLoading />
      </section>
    </div>
  </main>
);

const BookingsUnavailablePage = ({ brandSettings }) => (
  <main className="page page--booking">
    <header className="hero">
      <div className="hero-brand">
        {brandSettings?.logoUrl ? (
          <img
            className="brand-logo"
            src={brandSettings.logoUrl}
            alt={brandSettings.businessName || 'Brand logo'}
          />
        ) : null}
        <span className="brand-name">{brandSettings?.businessName || 'Build-A-Bear Workshop'}</span>
      </div>
      <h1>Bookings are temporarily unavailable</h1>
      <p className="subtitle">
        {brandSettings?.bookingsModeMessage ||
          'Online booking pages are hidden while bookings are blocked by an administrator.'}
      </p>
    </header>

    <section className="card card--soft stack">
      <p className="muted">Please return to the store locator and try again later.</p>
      <div>
        <a className="btn btn-primary" href="/">
          Return to store locator
        </a>
      </div>
    </section>
  </main>
);

const VERSION_CACHE_KEY = 'bab:last-seen-server-version';

const buildVersionSignature = (data) => {
  if (!data) return '';
  return [data.service, data.version, data.gitSha].filter(Boolean).join('@');
};

export default function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);

  useEffect(() => {
    initBookingAttribution();
  }, []);

  const pathname = window.location.pathname;
  const isConfirmation = pathname.startsWith('/confirmation/');
  const isCancelled = pathname.startsWith('/checkout/cancelled');
  const isOffsiteRoute = pathname.startsWith('/offsite/');
  const isTermsAndConditions =
    pathname === '/terms-and-conditions' || pathname === '/terms-and-conditions/';
  const brandSettingsQuery = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => apiRequest({ path: '/api/public/settings' }),
    retry: false,
  });
  const versionQuery = useQuery({
    queryKey: ['server-version'],
    queryFn: () => apiRequest({ path: '/api/version' }),
    retry: false,
    refetchInterval: 5 * 60 * 1000,
  });
  const brandSettings = brandSettingsQuery.data || null;
  const settingsLoaded = brandSettingsQuery.isSuccess;
  const bookingsAreBlocked = brandSettings ? !brandSettings.bookingsModeEnabled : false;
  const isBookingRoute = pathname.startsWith('/store/') || pathname.startsWith('/offsite/');
  const bookingRouteFallback = isOffsiteRoute ? <OffsiteRouteFallback /> : <BookingRouteFallback />;

  useEffect(() => {
    if (pathname.startsWith('/store/')) loadBookingPage();
    if (pathname.startsWith('/offsite/')) loadOffsitePage();
  }, [pathname]);

  useEffect(() => {
    const signature = buildVersionSignature(versionQuery.data);
    if (!signature) return;

    const previousSignature = window.localStorage.getItem(VERSION_CACHE_KEY);
    if (previousSignature && previousSignature !== signature) {
      setUpdateAvailable(true);
      setUpdateDismissed(false);
    }
    window.localStorage.setItem(VERSION_CACHE_KEY, signature);
  }, [versionQuery.data]);

  useEffect(() => {
    if (!brandSettings) return;
    const root = document.documentElement;
    setTenantFormattingDefaults({
      locale: brandSettings.locale,
      currency: brandSettings.currencyCode,
    });
    setTenantTimePreferences({
      baseTimezone: brandSettings.baseTimezone,
      locale: brandSettings.locale,
    });
    setAnalyticsMeasurementId(brandSettings.googleAnalyticsMeasurementId);
    setClarityProjectId(brandSettings.microsoftClarityId);
    initClarity();
    if (brandSettings.primaryColor) {
      root.style.setProperty('--accent', brandSettings.primaryColor);
    }
    if (brandSettings.secondaryColor) {
      root.style.setProperty('--accent-2', brandSettings.secondaryColor);
    }
  }, [brandSettings]);
  let route = <NotFoundPage brandSettings={brandSettings} />;

  if (isCancelled) {
    route = <CancelledPage brandSettings={brandSettings} />;
  } else if (isTermsAndConditions) {
    route = <TermsAndConditionsPage brandSettings={brandSettings} />;
  } else if (isConfirmation) {
    const bookingRef = pathname.replace('/confirmation/', '');
    route = !bookingRef ? <NotFoundPage brandSettings={brandSettings} /> : <ConfirmationPage bookingRef={bookingRef} brandSettings={brandSettings} />;
  } else if (isBookingRoute && brandSettingsQuery.isLoading) {
    route = bookingRouteFallback;
  } else if (isBookingRoute && bookingsAreBlocked) {
    route = <BookingsUnavailablePage brandSettings={brandSettings} />;
  } else if (pathname.startsWith('/offsite/')) {
    const slug = pathname.replace('/offsite/', '');
    route = !slug ? <NotFoundPage brandSettings={brandSettings} /> : <OffsitePage slug={slug} brandSettings={brandSettings} />;
  } else if (pathname.startsWith('/portal')) {
    route = <PortalPage brandSettings={brandSettings} />;
  } else if (pathname === '/events' || pathname === '/events/') {
    route = <EventsPage brandSettings={brandSettings} />;
  } else if (pathname.startsWith('/store/')) {
    const slug = pathname.replace('/store/', '');
    route = !slug ? (
      <NotFoundPage brandSettings={brandSettings} />
    ) : (
      <BookingPage brandSettings={brandSettings} initialStoreSlug={slug} />
    );
  } else if (pathname === '/') {
    route = <HomePage brandSettings={brandSettings} />;
  }

  return (
    <>
      {settingsLoaded && bookingsAreBlocked ? (
        <MaintenanceBanner message={brandSettings?.bookingsModeMessage} />
      ) : null}
      {updateAvailable && !updateDismissed ? (
        <UpdateBanner onDismiss={() => setUpdateDismissed(true)} />
      ) : null}
      <Suspense fallback={isBookingRoute ? bookingRouteFallback : <RouteFallback />}>
        {route}
      </Suspense>
    </>
  );
}
